import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import { toast } from "sonner";
import { api, formatApiDetail, resolveBackendOrigin } from "../lib/api";
import { BLUPreview, InvoiceSection } from "../components/BLUPreview";
import ProposalThanksScreen from "../components/ProposalThanksScreen";
import SignaturePad, { isSignatureDataUrl } from "../components/SignaturePad";
import { useAuth } from "../context/AuthContext";
import { isLocalApp } from "../lib/env";
import {
  acceptUploadedProposal,
  clearUploadedProposalDecision,
  clientIdFromUploadToken,
  getUploadedProposal,
  getUploadedProposalBySlug,
  isUploadShareToken,
  markProposalOpened,
  packetFromPublicProposal,
  requestChangesUploadedProposal,
} from "../lib/proposalUploadMock";
import { slugifyClientName } from "../lib/portalPaths";

/** Local demo — open /scope/preview (no API, no login). */
const PREVIEW_BASE = {
  client_name: "Harbor & Pine",
  primary_contact_name: "Jules Maren",
  date_range: "Sep 1 – Oct 15, 2026",
  issued_date: "Aug 7, 2026",
  due_date: "Aug 21, 2026",
  credits: 0,
};

const PREVIEW_OPTIONS = [
  {
    ...PREVIEW_BASE,
    option_label: "Option 1.",
    intro:
      "This document outlines the scope of our operational partnership for Harbor & Pine — a focused identity phase to ready the ceramics line for retail conversations this fall.",
    scope_items: [
      {
        number: "01",
        title: "Brand Identity System",
        description:
          "Quieter wordmark, type, and color system. Direction held from discovery: less high-contrast, more material honesty.",
      },
    ],
    investment_total: 12000,
    subtotal: 12000,
    total: 12000,
  },
  {
    ...PREVIEW_BASE,
    option_label: "Option 2.",
    intro:
      "This document outlines the scope of our operational partnership for Harbor & Pine — a phased brand and web build designed to ready the ceramics line for retail pitches this fall.",
    scope_items: [
      {
        number: "01",
        title: "Brand Identity System",
        description:
          "Quieter wordmark, type, and color system across packaging cues. Direction held from discovery: less high-contrast, more material honesty.",
      },
      {
        number: "02",
        title: "Website Design + Development",
        description:
          "Product grid, CMS, and a clean path from story to shop. Built to support Q4 partner samples without a full rebuild later.",
      },
    ],
    investment_total: 18000,
    subtotal: 18000,
    total: 18000,
    addon: {
      title: "Packaging starter set",
      description: "Production-ready layouts for the first three SKUs, sized for your existing print partners.",
      amount: 3200,
    },
  },
  {
    ...PREVIEW_BASE,
    option_label: "Option 3.",
    intro:
      "This document outlines the scope of our operational partnership for Harbor & Pine — a full phased build through packaging templates for retail-ready samples.",
    scope_items: [
      {
        number: "01",
        title: "Brand Identity System",
        description: "Full system across wordmark, type, color, and packaging cues.",
      },
      {
        number: "02",
        title: "Website Design + Development",
        description: "Product grid, CMS, and path from story to shop.",
      },
      {
        number: "03",
        title: "Packaging Templates",
        description: "Production-ready layouts for the first SKU set, aligned to the identity system.",
      },
    ],
    investment_total: 24000,
    subtotal: 24000,
    total: 24000,
  },
];

/** Local demo — open /scope/barter (no API). Barter agreement (any offering; ops shown as monthly scope without invoice). */
const BARTER_PREVIEW_OPTION = {
  option_label: "Agreement",
  client_name: "Lumen & Clay",
  compensation_type: "barter",
  timeline: "Ongoing",
  date_range: "Ongoing",
  intro:
    "This agreement sets a barter partnership. There is no invoice — for operational ecosystem work, each cycle we share a fresh scope of what we’re working on together, the same way a paid monthly partnership runs.",
  scope_items: [
    {
      number: "01",
      title: "Monthly scope of work",
      description:
        "At the start of each cycle, TAĪSTU sets the month’s focus (systems, site, brand ops, or handoff) in the portal — priorities held with flexibility as the month unfolds.",
    },
    {
      number: "02",
      title: "Shared portal rhythm",
      description:
        "Phases, milestones, and updates live in the client portal so both sides stay aligned without invoice gates.",
    },
  ],
  investment_total: 0,
  subtotal: 0,
  total: 0,
  exchange:
    "In place of monthly fees: one studio photoshoot day per quarter (products + founder) and ongoing product credit for TAĪSTU portfolio and client gifts, valued in line with a monthly operational retainer.",
  terms:
    "Engagement may be short-term or ongoing. Either party can pause at the end of a monthly cycle with notice. Monthly scopes replace invoices; acceptance of this agreement locks the partnership.",
};

/** Horizontal swipe: proposals between options; invoice is always page 2 of the selected option. */
function ScopeOptionsCarousel({
  options,
  index,
  onIndexChange,
  locked = false,
  sectionTitle = "Scope of Support",
  addonSelected = false,
  onAddonChange,
  canSelectAddon = false,
  /** Cash: page 2 is that option’s invoice. Barter stays proposal-only. */
  showInvoice = false,
  invoiceLocked = true,
}) {
  const slideRefs = useRef([]);
  const [trackHeight, setTrackHeight] = useState(null);
  /** 0 = proposal, 1 = invoice (only when showInvoice). */
  const [page, setPage] = useState(0);
  const optionCount = options.length;
  const activeIndex = locked
    ? Math.max(0, Math.min(optionCount - 1, index))
    : Math.max(0, Math.min(optionCount - 1, index));
  const activePayload = options[activeIndex] || options[0] || null;
  const activeAddon = activePayload?.addon || null;
  const pageCount = showInvoice && activePayload ? 2 : 1;
  const safePage = Math.min(page, pageCount - 1);

  const pages = useMemo(() => {
    if (!activePayload) return [];
    if (!showInvoice) {
      return [{ kind: "proposal", payload: activePayload }];
    }
    return [
      { kind: "proposal", payload: activePayload },
      { kind: "invoice", payload: activePayload },
    ];
  }, [activePayload, showInvoice]);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    dragFree: false,
    loop: false,
    skipSnaps: false,
    watchDrag: pageCount > 1,
  });

  // Changing option always returns to the proposal page.
  useEffect(() => {
    setPage(0);
  }, [activeIndex, showInvoice]);

  const measureActive = useCallback(() => {
    const slide = slideRefs.current[safePage];
    if (!slide) return;
    setTrackHeight(slide.offsetHeight);
  }, [safePage]);

  useEffect(() => {
    measureActive();
    const slide = slideRefs.current[safePage];
    if (!slide || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => measureActive());
    ro.observe(slide);
    return () => ro.disconnect();
  }, [safePage, pages, measureActive, addonSelected, activeAddon, invoiceLocked]);

  useEffect(() => {
    if (!emblaApi) return;
    if (emblaApi.selectedScrollSnap() !== safePage) {
      emblaApi.scrollTo(safePage, true);
    }
  }, [emblaApi, safePage]);

  useEffect(() => {
    if (!emblaApi) return undefined;
    const onSelect = () => {
      const next = emblaApi.selectedScrollSnap();
      if (next !== page) setPage(next);
    };
    const onSettle = () => measureActive();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    emblaApi.on("settle", onSettle);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
      emblaApi.off("settle", onSettle);
    };
  }, [emblaApi, page, measureActive]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.reInit({ watchDrag: pageCount > 1 });
  }, [emblaApi, pageCount, pages.length, activeIndex]);

  const goToOption = useCallback(
    (next) => {
      if (locked) return;
      const clamped = Math.max(0, Math.min(optionCount - 1, next));
      onIndexChange(clamped);
      setPage(0);
    },
    [locked, optionCount, onIndexChange],
  );

  if (!activePayload) return null;

  const showOptionChrome = !locked && optionCount > 1;

  return (
    <div className="relative" data-testid="scope-options-carousel">
      {showOptionChrome ? (
        <div className="mb-3 flex items-center justify-between gap-3">
          <button
            type="button"
            disabled={activeIndex <= 0}
            onClick={() => goToOption(activeIndex - 1)}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[var(--t-accent)] text-[var(--t-accent)] transition-opacity disabled:opacity-30"
            aria-label="Previous option"
            data-testid="scope-option-prev"
          >
            <ChevronLeft size={18} strokeWidth={1.4} />
          </button>
          <div className="min-w-0 text-center text-[10px] uppercase tracking-[0.14em] text-[var(--t-meta)]">
            {activePayload.option_label || `Option ${activeIndex + 1}`}
            <span className="text-[var(--t-meta)]"> · {activeIndex + 1} / {optionCount}</span>
          </div>
          <button
            type="button"
            disabled={activeIndex >= optionCount - 1}
            onClick={() => goToOption(activeIndex + 1)}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[var(--t-accent)] text-[var(--t-accent)] transition-opacity disabled:opacity-30"
            aria-label="Next option"
            data-testid="scope-option-next"
          >
            <ChevronRight size={18} strokeWidth={1.4} />
          </button>
        </div>
      ) : null}

      <div
        ref={emblaRef}
        className="overflow-hidden"
        style={{
          height: trackHeight != null ? trackHeight : undefined,
          transition: "height 200ms ease",
        }}
        data-testid="scope-options-scroller"
      >
        <div className="flex touch-pan-y">
          {pages.map((entry, i) => (
            <div
              key={`${activeIndex}-${entry.kind}`}
              ref={(node) => {
                slideRefs.current[i] = node;
              }}
              className="min-w-0 shrink-0 grow-0 basis-full"
              data-testid={`scope-option-slide-${activeIndex}-${entry.kind}`}
            >
              <div className="overflow-hidden rounded-2xl border border-[var(--t-border)] bg-white p-2 sm:p-4">
                {entry.kind === "proposal" ? (
                  <BLUPreview
                    payload={entry.payload}
                    interactive={false}
                    scopeOnly
                    sectionTitle={sectionTitle}
                    showAddonControls
                    addonSelected={Boolean(addonSelected)}
                    onAddonChange={onAddonChange}
                    canSelectAddon={canSelectAddon}
                  />
                ) : (
                  <div
                    className="blu-document-preview"
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      background: "#FCFCFC",
                      color: "#222222",
                      borderRadius: 12,
                      padding: "22px 22px 20px",
                    }}
                    data-testid="scope-invoice-page"
                  >
                    <InvoiceSection
                      payload={entry.payload}
                      locked={Boolean(invoiceLocked)}
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {showOptionChrome ? (
        <div className="mt-6 flex items-center justify-center gap-1.5">
          {options.map((opt, i) => (
            <button
              key={opt.option_label || i}
              type="button"
              onClick={() => goToOption(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === activeIndex ? "w-5 bg-[var(--t-accent)]" : "w-1.5 bg-[var(--t-border)]"
              }`}
              aria-label={`Go to ${opt.option_label || `option ${i + 1}`}`}
              data-testid={`scope-option-dot-${i}`}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function formatStamp(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function ScopeReviewShell({
  clientName,
  status,
  acceptedAt,
  acceptedOptionLabel,
  changesRequestedAt,
  responseNote,
  signatureName,
  signedAt,
  canRespond,
  canDownloadPdf,
  documentNode,
  onDownload,
  note,
  setNote,
  signature,
  setSignature,
  busy,
  onAccept,
  onRequestChanges,
  acceptLabel,
  studioToggle,
  title = "Scope of Support",
  /** Barter: signature box only (no Note / Request changes). */
  signToAccept = false,
}) {
  const changesStamp = formatStamp(changesRequestedAt);
  const acceptedStamp = formatStamp(signedAt || acceptedAt);
  const showChangesNote =
    !signToAccept &&
    status === "changes_requested" &&
    (responseNote || note || !canRespond);
  const showProposalActions = !signToAccept && (canRespond || showChangesNote);
  const showSignBox = signToAccept && (canRespond || status === "accepted");
  const signatureReady = Boolean(signature && (isSignatureDataUrl(signature) || String(signature).trim()));

  return (
    <div className="min-h-screen bg-[var(--t-bg)]" data-testid="scope-review-page">
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 md:py-14">
        {studioToggle ? (
          <div className="mb-6 flex flex-wrap items-center justify-end gap-3">{studioToggle}</div>
        ) : null}
        <div className="label-eyebrow mb-2">TAĪSTU</div>
        <h1 className="font-heading text-3xl tracking-tight !font-extralight md:text-4xl">
          {title}
        </h1>
        <p className="mt-2 text-sm text-[var(--t-meta)]">{clientName}</p>

        <div className="mt-8">{documentNode}</div>

        {canDownloadPdf && onDownload ? (
          <button
            type="button"
            onClick={onDownload}
            className="neo-cta mt-6 inline-flex"
            data-testid="scope-review-download"
          >
            Download PDF
          </button>
        ) : null}

        {status === "accepted" && !signToAccept ? (
          <p
            className="mt-6 text-center text-sm text-[var(--t-accent)]"
            data-testid="scope-review-accepted"
          >
            Accepted
            {acceptedOptionLabel ? ` · ${acceptedOptionLabel}` : ""}
            {acceptedStamp ? ` · ${acceptedStamp}` : ""}.
          </p>
        ) : null}

        {showSignBox ? (
          <section className="mt-8 space-y-4" data-testid="scope-review-sign">
            <div className="label-eyebrow">Signature</div>
            {status === "accepted" ? (
              <div
                className="border-b border-[var(--t-border)] pb-3"
                data-testid="scope-review-signed"
              >
                {signatureName && isSignatureDataUrl(signatureName) ? (
                  <img
                    src={signatureName}
                    alt="Signature"
                    className="max-h-16 max-w-full object-contain"
                    data-testid="scope-review-signature-image"
                  />
                ) : (
                  <p
                    className="font-heading text-2xl !font-extralight tracking-tight text-[var(--t-text)]"
                    data-testid="scope-review-signature-name"
                  >
                    {signatureName || "Signed"}
                  </p>
                )}
                <p
                  className="mt-2 min-h-[1.25rem] text-sm text-[var(--t-accent)]"
                  data-testid="scope-review-signature-stamp"
                >
                  {acceptedStamp || "\u00a0"}
                </p>
              </div>
            ) : (
              <>
                <SignaturePad
                  value={signature || null}
                  onChange={(next) => setSignature?.(next || "")}
                  disabled={busy || !canRespond}
                  label="Sign with your finger or cursor"
                  data-testid="scope-review-signature-pad"
                />
                <div className="flex justify-center md:justify-start">
                  <button
                    type="button"
                    disabled={busy || !signatureReady}
                    onClick={onAccept}
                    className="neo-cta disabled:opacity-50"
                    data-testid="scope-review-sign-submit"
                  >
                    {busy ? "Signing…" : acceptLabel || "Sign agreement"}
                  </button>
                </div>
              </>
            )}
          </section>
        ) : null}

        {showProposalActions ? (
          <section className="mt-6 space-y-4" data-testid="scope-review-actions">
            <label className="block">
              <span className="label-eyebrow mb-2 block">
                {status === "changes_requested" ? "Notes" : "Note"}
              </span>
              <textarea
                className="t-textarea min-h-[100px] w-full !rounded-[var(--t-event-pill-radius)]"
                value={status === "changes_requested" ? responseNote || note : note}
                onChange={(e) => (canRespond ? setNote(e.target.value) : undefined)}
                readOnly={!canRespond}
                placeholder="Anything you’d like to add, or changes you’d like to request."
                data-testid="scope-review-note"
              />
            </label>
            {status === "changes_requested" ? (
              <p
                className="text-center text-sm text-[var(--t-warn)]"
                data-testid="scope-review-changes"
              >
                Changes requested
                {changesStamp ? ` · ${changesStamp}` : ""}.
              </p>
            ) : null}
            {canRespond ? (
              <div className="flex flex-wrap justify-center gap-3 md:justify-start">
                <button
                  type="button"
                  disabled={busy}
                  onClick={onAccept}
                  className="neo-cta disabled:opacity-50"
                  data-testid="scope-review-accept"
                >
                  {acceptLabel || "Accept scope"}
                </button>
                <button
                  type="button"
                  disabled={busy || !String(note || "").trim()}
                  onClick={onRequestChanges}
                  className="inline-flex min-h-10 items-center rounded-[var(--t-event-pill-radius)] border border-[var(--t-border)] px-4 text-[11px] uppercase tracking-[0.14em] text-[var(--t-meta)] hover:text-[var(--t-text)] disabled:opacity-50"
                  data-testid="scope-review-request-changes"
                >
                  Request changes
                </button>
              </div>
            ) : null}
          </section>
        ) : null}

        <p className="mt-12 text-center text-[11px] tracking-[0.08em] text-[var(--t-meta)]">
          <a href="/privacy" className="hover:text-[var(--t-accent)]" data-testid="scope-privacy-link">
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  );
}

function ScopeReviewPreview() {
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("pending");
  const [acceptedAt, setAcceptedAt] = useState(null);
  const [changesAt, setChangesAt] = useState(null);
  const [responseNote, setResponseNote] = useState(null);
  const [optionIndex, setOptionIndex] = useState(0);
  const [acceptedOptionIndex, setAcceptedOptionIndex] = useState(null);
  const [studioView, setStudioView] = useState(false);
  const [addonSelected, setAddonSelected] = useState(false);
  const current = PREVIEW_OPTIONS[optionIndex] || PREVIEW_OPTIONS[0];
  const acceptedLabel =
    acceptedOptionIndex != null
      ? PREVIEW_OPTIONS[acceptedOptionIndex]?.option_label || `Option ${acceptedOptionIndex + 1}`
      : null;
  const lockedForProspect = status === "accepted" && !studioView;
  const carouselIndex =
    lockedForProspect && acceptedOptionIndex != null ? acceptedOptionIndex : optionIndex;

  useEffect(() => {
    setAddonSelected(false);
  }, [optionIndex]);

  const resetSelection = () => {
    setStatus("pending");
    setAcceptedAt(null);
    setChangesAt(null);
    setResponseNote(null);
    setNote("");
    setOptionIndex(0);
    setAcceptedOptionIndex(null);
    setStudioView(false);
    setAddonSelected(false);
  };

  return (
    <ScopeReviewShell
      studioToggle={
        status === "accepted" || status !== "pending" ? (
          <>
            {status === "accepted" ? (
              <button
                type="button"
                onClick={() => setStudioView((v) => !v)}
                className="text-[11px] uppercase tracking-[0.14em] text-[var(--t-accent)]"
                data-testid="scope-preview-studio-toggle"
              >
                {studioView ? "Prospect view" : "Studio: view all options"}
              </button>
            ) : null}
            {status !== "pending" ? (
              <button
                type="button"
                onClick={resetSelection}
                className="text-[11px] uppercase tracking-[0.14em] text-[var(--t-meta)]"
                data-testid="scope-preview-reset"
              >
                Reset selection
              </button>
            ) : null}
          </>
        ) : null
      }
      clientName={current.client_name}
      status={status}
      acceptedAt={acceptedAt}
      acceptedOptionLabel={acceptedLabel}
      changesRequestedAt={changesAt}
      responseNote={responseNote}
      canRespond={status === "pending"}
      canDownloadPdf={status === "accepted"}
      acceptLabel="Accept scope"
      onDownload={() => toast.message("In production this downloads the accepted PDF.")}
      note={note}
      setNote={setNote}
      busy={false}
      onAccept={() => {
        setStatus("accepted");
        setAcceptedAt(new Date().toISOString());
        setAcceptedOptionIndex(optionIndex);
        setStudioView(false);
        setResponseNote(note.trim() || null);
        toast.success(`Accepted ${current.option_label || `Option ${optionIndex + 1}`} (preview).`);
      }}
      onRequestChanges={() => {
        const context = note.trim();
        if (!context) {
          toast.error("Add a note with what you’d like to share or change.");
          return;
        }
        setStatus("changes_requested");
        setChangesAt(new Date().toISOString());
        setResponseNote(context);
        setAcceptedOptionIndex(null);
        setOptionIndex(0);
        setStudioView(false);
        setAddonSelected(false);
        toast.success("Change request sent — note saved to Notes (preview).");
      }}
      documentNode={
        <ScopeOptionsCarousel
          options={PREVIEW_OPTIONS}
          index={carouselIndex}
          onIndexChange={setOptionIndex}
          locked={lockedForProspect}
          showInvoice
          invoiceLocked={status !== "accepted"}
          addonSelected={addonSelected}
          onAddonChange={setAddonSelected}
          canSelectAddon={status === "pending"}
        />
      }
    />
  );
}

/** Local barter agreement preview — /scope/barter (sign → thanks). Also /scope/barter-thanks. */
function BarterAgreementPreview({ initialScreen = "agreement" } = {}) {
  const [signature, setSignature] = useState("");
  const [status, setStatus] = useState(initialScreen === "thanks" ? "accepted" : "pending");
  const [acceptedAt, setAcceptedAt] = useState(
    initialScreen === "thanks" ? new Date().toISOString() : null,
  );
  const [signatureName, setSignatureName] = useState(
    initialScreen === "thanks" ? "Maya Chen" : null,
  );
  const [studioScreen, setStudioScreen] = useState(
    initialScreen === "thanks" ? "thanks" : "agreement",
  );
  const opt = BARTER_PREVIEW_OPTION;

  useEffect(() => {
    document.title = `${opt.client_name} x TAĪSTU`;
    return () => {
      document.title = "TAĪSTU | Client Portal";
    };
  }, [opt.client_name]);

  const showThanks = studioScreen === "thanks" || status === "accepted";

  const studioToggle = (
    <div className="flex flex-wrap items-center gap-3" data-testid="scope-barter-studio-mock">
      <button
        type="button"
        onClick={() => {
          setStudioScreen("agreement");
          setStatus("pending");
          setAcceptedAt(null);
          setSignatureName(null);
          setSignature("");
        }}
        className={`text-[11px] uppercase tracking-[0.14em] ${
          !showThanks ? "text-[var(--t-accent)]" : "text-[var(--t-meta)]"
        }`}
        data-testid="scope-barter-mock-agreement"
      >
        Agreement
      </button>
      <button
        type="button"
        onClick={() => {
          setStudioScreen("thanks");
          setStatus("accepted");
          setAcceptedAt(new Date().toISOString());
          setSignatureName(signature.trim() || "Maya Chen");
        }}
        className={`text-[11px] uppercase tracking-[0.14em] ${
          showThanks ? "text-[var(--t-accent)]" : "text-[var(--t-meta)]"
        }`}
        data-testid="scope-barter-mock-thanks"
      >
        Thanks
      </button>
    </div>
  );

  if (showThanks) {
    return (
      <>
        <div className="fixed left-0 right-0 top-0 z-20 flex flex-wrap items-center justify-end gap-3 px-4 py-3">
          {studioToggle}
        </div>
        <ProposalThanksScreen
          clientName={opt.client_name}
          optionLabel={opt.option_label}
          personName={signatureName && !isSignatureDataUrl(signatureName) ? signatureName : "Maya Chen"}
          variant="barter"
        />
      </>
    );
  }

  return (
    <ScopeReviewShell
      title="Agreement"
      studioToggle={studioToggle}
      clientName={opt.client_name}
      status={status}
      acceptedAt={acceptedAt}
      signedAt={acceptedAt}
      signatureName={signatureName}
      acceptedOptionLabel={status === "accepted" ? opt.option_label : null}
      canRespond={status === "pending"}
      canDownloadPdf={false}
      acceptLabel="Sign agreement"
      signToAccept
      signature={signature}
      setSignature={setSignature}
      busy={false}
      onAccept={() => {
        const stroke = signature;
        if (!stroke) {
          toast.error("Draw your signature to continue.");
          return;
        }
        const stamped = new Date().toISOString();
        setStatus("accepted");
        setAcceptedAt(stamped);
        setSignatureName(stroke);
        setStudioScreen("thanks");
        toast.success("Agreement signed (preview).");
      }}
      documentNode={
        <div data-testid="scope-barter-preview-document">
          <ScopeOptionsCarousel
            options={[opt]}
            index={0}
            onIndexChange={() => {}}
            locked
            sectionTitle="Agreement"
            canSelectAddon={false}
            showInvoice={false}
          />
        </div>
      }
    />
  );
}

/** Public live proposal from API (no login). Accept / request changes hit the server. */
function PublicLiveProposalReview({ slug, data, onReload }) {
  const { user } = useAuth() || {};
  const isStudio = user?.role === "admin";
  const [packet, setPacket] = useState(() => packetFromPublicProposal(data));
  const [note, setNote] = useState(data?.response_note || "");
  const [signature, setSignature] = useState("");
  const [optionIndex, setOptionIndex] = useState(0);
  const [addonSelected, setAddonSelected] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setPacket(packetFromPublicProposal(data));
    if (data?.response_note) setNote(data.response_note);
    const company = (data?.client_name || "").trim();
    if (company) {
      document.title = `${company} x TAĪSTU`;
    }
    return () => {
      document.title = "TAĪSTU | Client Portal";
    };
  }, [data]);

  useEffect(() => {
    setAddonSelected(false);
  }, [optionIndex]);

  if (!packet?.options?.length) {
    return (
      <div className="min-h-screen bg-[var(--t-bg)] p-6 md:p-12" data-testid="scope-public-missing">
        <p className="text-sm text-[var(--t-error)]">Proposal not found</p>
      </div>
    );
  }

  const options = (packet.options || []).map((opt) => ({
    ...opt,
    primary_contact_name:
      opt.primary_contact_name || packet.contactName || data?.primary_contact_name || null,
    contact_name: opt.contact_name || packet.contactName || data?.primary_contact_name || null,
    invoice_number: opt.invoice_number || data?.invoice_number || packet.invoiceNumber || null,
    issued_date: opt.issued_date || data?.issued_date || null,
    due_date: opt.due_date || data?.due_date || null,
    invoice_service_title: opt.invoice_service_title || data?.invoice_service_title || null,
    invoice_service_description:
      opt.invoice_service_description || data?.invoice_service_description || null,
    already_paid: Boolean(opt.already_paid || opt.paid || data?.already_paid || data?.paid),
    paid: Boolean(opt.already_paid || opt.paid || data?.already_paid || data?.paid),
    paid_at: opt.paid_at || data?.paid_at || packet.paidAt || null,
  }));
  const status = packet.status || "pending";
  const current = options[optionIndex] || options[0];
  const acceptedLabel =
    status === "accepted"
      ? packet.acceptedOptionLabel ||
        options[packet.acceptedOptionIndex ?? optionIndex]?.option_label ||
        options[0]?.option_label ||
        "Option"
      : null;
  const lockedForProspect = status === "accepted";
  const carouselIndex =
    lockedForProspect && packet.acceptedOptionIndex != null
      ? packet.acceptedOptionIndex
      : optionIndex;
  const canRespond = Boolean(data?.can_respond) && !isStudio && status === "pending";
  const personName = packet.contactName || "";
  const isBarter = (packet.compensationType || data?.compensation_type) === "barter";
  const reviewKind =
    data?.review_kind ||
    packet.reviewKind ||
    (isBarter ? "agreement" : "proposal");
  const docTitle =
    reviewKind === "agreement" ? "Agreement" : reviewKind === "scope" ? "Scope" : "Proposal";
  const companyName = packet.clientName || current?.client_name || "Prospect";
  const clientLine =
    personName && companyName ? `${personName} • ${companyName}` : companyName;
  const invoiceUnlocked = Boolean(
    packet.invoiceUnlocked ?? data?.invoice_unlocked ?? (status === "accepted" && !isBarter),
  );

  const respond = async (action) => {
    if (!canRespond || busy) return;
    const context = note.trim();
    const sig = signature.trim();
    if (isBarter && action === "accept" && !sig) {
      toast.error("Draw your signature to continue.");
      return;
    }
    if (action === "request_changes" && !context) {
      toast.error("Add a note with what you’d like to share or change.");
      return;
    }
    setBusy(true);
    try {
      const opt = options[optionIndex] || options[0];
      const path = `/public/proposal/${encodeURIComponent(slugifyClientName(slug))}/respond`;
      const { data: row } = await api.post(path, {
        action,
        note: isBarter ? null : context || null,
        signature: isBarter && action === "accept" ? sig : null,
        option_index: optionIndex,
        option_label: opt?.option_label || null,
        addon_selected: Boolean(addonSelected && opt?.addon),
        accepted_addon: addonSelected && opt?.addon ? opt.addon : null,
      });
      setPacket(packetFromPublicProposal(row));
      if (row?.response_note) setNote(row.response_note);
      onReload?.(row);
      toast.success(
        action === "accept"
          ? isBarter
            ? "Agreement signed."
            : reviewKind === "scope"
              ? "Scope accepted."
              : `Accepted ${row?.accepted_option_label || opt?.option_label || "option"}.`
          : "Change request sent — we’ll adjust and follow up.",
      );
    } catch (e) {
      toast.error(formatApiDetail(e.response?.data?.detail, "Could not send response"));
    } finally {
      setBusy(false);
    }
  };

  if (status === "accepted") {
    if (isBarter) {
      return (
        <ProposalThanksScreen
          clientName={packet.clientName || current?.client_name || "Prospect"}
          optionLabel={acceptedLabel}
          personName={packet.signatureName || personName}
          variant="barter"
        />
      );
    }
    // Cash: stay on the proposal — invoice unlocks in place (no thanks screen).
  }

  return (
    <ScopeReviewShell
      title={docTitle}
      clientName={clientLine}
      status={status}
      acceptedAt={packet.acceptedAt}
      signedAt={packet.signedAt}
      signatureName={packet.signatureName}
      acceptedOptionLabel={acceptedLabel}
      changesRequestedAt={packet.changesRequestedAt}
      responseNote={packet.responseNote}
      canRespond={canRespond}
      canDownloadPdf={false}
      acceptLabel={isBarter ? "Sign agreement" : reviewKind === "scope" ? "Accept scope" : "Accept this option"}
      signToAccept={isBarter}
      note={note}
      setNote={setNote}
      signature={signature}
      setSignature={setSignature}
      busy={busy}
      onAccept={() => respond("accept")}
      onRequestChanges={() => respond("request_changes")}
      documentNode={
        <div data-testid="scope-public-document">
          <ScopeOptionsCarousel
            options={options}
            index={carouselIndex}
            onIndexChange={setOptionIndex}
            locked={lockedForProspect || isBarter}
            sectionTitle={docTitle}
            addonSelected={addonSelected}
            onAddonChange={setAddonSelected}
            canSelectAddon={canRespond && !isBarter}
            showInvoice={!isBarter}
            invoiceLocked={!invoiceUnlocked}
          />
        </div>
      }
    />
  );
}

/** Local: uploaded multi-option proposal shared via /{company}/proposal */
function UploadedProposalReview({ clientId }) {
  const { user } = useAuth() || {};
  const isStudio = user?.role === "admin";
  const showStudioMocks = isStudio && isLocalApp();
  const [packet, setPacket] = useState(() => getUploadedProposal(clientId));
  const [note, setNote] = useState("");
  const [optionIndex, setOptionIndex] = useState(0);
  const [studioView, setStudioView] = useState(false);
  /** Localhost studio only: which screen to preview. Default proposal (not thanks). */
  const [studioMock, setStudioMock] = useState("pending");
  const [addonSelected, setAddonSelected] = useState(false);

  useEffect(() => {
    // Only prospect opens stamp activity — studio preview must not.
    const row = isStudio
      ? getUploadedProposal(clientId)
      : markProposalOpened(clientId) || getUploadedProposal(clientId);
    setPacket(row);
    const company = (row?.clientName || "").trim();
    if (company) document.title = `${company} x TAĪSTU`;
    return () => {
      document.title = "TAĪSTU | Client Portal";
    };
  }, [clientId, isStudio]);

  useEffect(() => {
    setAddonSelected(false);
  }, [optionIndex]);

  if (!packet) {
    return (
      <div className="min-h-screen bg-[var(--t-bg)] p-6 md:p-12" data-testid="scope-uploaded-missing">
        <p className="text-sm text-[var(--t-error)]">
          This uploaded proposal link isn’t available. Upload a proposal on the prospect record first.
        </p>
      </div>
    );
  }

  const options = (packet.options || []).map((opt) => ({
    ...opt,
    primary_contact_name: opt.primary_contact_name || packet.contactName || null,
    contact_name: opt.contact_name || packet.contactName || null,
  }));
  const liveStatus = packet.status || "pending";
  const current = options[optionIndex] || options[0];

  // Localhost studio can preview thanks/changes; everywhere else use live status.
  const status = showStudioMocks ? studioMock : liveStatus;
  const acceptedLabel =
    status === "accepted"
      ? packet.acceptedOptionLabel ||
        options[packet.acceptedOptionIndex ?? optionIndex]?.option_label ||
        options[0]?.option_label ||
        "Option 1 · Essential"
      : null;
  const acceptedAt =
    status === "accepted"
      ? packet.acceptedAt || new Date(Date.now() - 2 * 3600 * 1000).toISOString()
      : null;
  const changesAt =
    status === "changes_requested"
      ? packet.changesRequestedAt || new Date(Date.now() - 90 * 60 * 1000).toISOString()
      : null;
  const changesNote =
    status === "changes_requested"
      ? packet.responseNote ||
        "Could we phase packaging templates into a second invoice? Identity + web first feels right for Q4."
      : packet.responseNote;
  const mockAcceptedIndex =
    packet.acceptedOptionIndex != null ? packet.acceptedOptionIndex : optionIndex;
  const lockedForProspect = status === "accepted" && !studioView;
  const carouselIndex =
    lockedForProspect && mockAcceptedIndex != null ? mockAcceptedIndex : optionIndex;
  const canRespond = !isStudio && liveStatus === "pending";

  const studioToggle = showStudioMocks ? (
    <div className="flex flex-wrap items-center gap-3" data-testid="scope-uploaded-studio-mock">
      <button
        type="button"
        onClick={() => {
          setStudioMock("pending");
          setStudioView(false);
        }}
        className={`text-[11px] uppercase tracking-[0.14em] ${
          studioMock === "pending" ? "text-[var(--t-accent)]" : "text-[var(--t-meta)]"
        }`}
        data-testid="scope-studio-mock-proposal"
      >
        Proposal
      </button>
      <button
        type="button"
        onClick={() => {
          setStudioMock("accepted");
          setStudioView(false);
        }}
        className={`text-[11px] uppercase tracking-[0.14em] ${
          studioMock === "accepted" ? "text-[var(--t-accent)]" : "text-[var(--t-meta)]"
        }`}
        data-testid="scope-studio-mock-accepted"
      >
        Accepted
      </button>
      <button
        type="button"
        onClick={() => {
          setStudioMock("changes_requested");
          setStudioView(false);
        }}
        className={`text-[11px] uppercase tracking-[0.14em] ${
          studioMock === "changes_requested" ? "text-[var(--t-accent)]" : "text-[var(--t-meta)]"
        }`}
        data-testid="scope-studio-mock-changes"
      >
        Changes
      </button>
    </div>
  ) : null;

  return (
    <ScopeReviewShell
      title="Proposal"
      studioToggle={studioToggle}
      clientName={packet.clientName || current?.client_name || "Prospect"}
      status={status}
      acceptedAt={acceptedAt}
      acceptedOptionLabel={acceptedLabel}
      changesRequestedAt={changesAt}
      responseNote={changesNote}
      canRespond={canRespond}
      canDownloadPdf={!isStudio && status === "accepted" && Boolean(options[carouselIndex]?.pdf_data_url || options.some((o) => o.pdf_data_url))}
      acceptLabel="Accept this option"
      onDownload={() => {
        const opt =
          options[packet.acceptedOptionIndex ?? carouselIndex] ||
          options.find((o) => o.pdf_data_url);
        const url = opt?.pdf_data_url;
        if (!url) {
          toast.message("No PDF attached to this option.");
          return;
        }
        const a = document.createElement("a");
        a.href = url;
        a.download = opt.originalFileName || `${packet.fileName || "proposal"}.pdf`;
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
      }}
      note={note}
      setNote={setNote}
      busy={false}
      onAccept={() => {
        if (isStudio) return;
        const next = acceptUploadedProposal(clientId, optionIndex, note, addonSelected);
        setPacket(next);
        setStudioView(false);
        toast.success(`Accepted ${next?.acceptedOptionLabel || "option"}.`);
      }}
      onRequestChanges={() => {
        if (isStudio) return;
        const context = note.trim();
        if (!context) {
          toast.error("Add a note with what you’d like to share or change.");
          return;
        }
        const next = requestChangesUploadedProposal(clientId, context);
        setPacket(next);
        setNote(context);
        setAddonSelected(false);
        toast.success("Change request sent.");
      }}
      documentNode={
        <div data-testid="scope-uploaded-document">
          <ScopeOptionsCarousel
            options={options}
            index={carouselIndex}
            onIndexChange={setOptionIndex}
            locked={lockedForProspect}
            sectionTitle="Proposal"
            addonSelected={addonSelected}
            onAddonChange={setAddonSelected}
            canSelectAddon={canRespond}
            showInvoice
            invoiceLocked={status !== "accepted"}
          />
        </div>
      }
    />
  );
}

export default function ScopeReviewPage() {
  const { token, clientSlug } = useParams();
  const { user } = useAuth() || {};
  const isStudio = user?.role === "admin";
  const showStudioMocks = isStudio && isLocalApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [signature, setSignature] = useState("");
  const [error, setError] = useState("");
  const [studioMock, setStudioMock] = useState("pending");

  const slug = (clientSlug || "").trim();
  const isPreview = token === "preview" && isLocalApp();
  const isBarterPreview = (token === "barter" || token === "barter-thanks") && isLocalApp();
  const isBarterThanksPreview = token === "barter-thanks" && isLocalApp();
  const localPacket =
    (slug && getUploadedProposalBySlug(slug)) ||
    (isUploadShareToken(token) ? getUploadedProposal(clientIdFromUploadToken(token)) : null);
  const uploadedClientId = localPacket?.clientId || null;

  const load = useCallback(async () => {
    if (isPreview || isBarterPreview) return;
    setLoading(true);
    setError("");
    try {
      if (slug) {
        const { data: row } = await api.get(
          `/public/proposal/${encodeURIComponent(slugifyClientName(slug))}`,
        );
        setData(row);
        if (row?.response_note) setNote(row.response_note);
        return;
      }
      if (token) {
        const { data: row } = await api.get(`/public/scope/${token}`);
        setData(row);
        if (row?.response_note) setNote(row.response_note);
        return;
      }
      setError("This proposal link is unavailable.");
      setData(null);
    } catch (e) {
      // Localhost-only fallback when the proposal was never published to the server.
      if (uploadedClientId && isLocalApp()) {
        setData(null);
        setError("");
        return;
      }
      setError(
        formatApiDetail(
          e.response?.data?.detail,
          "Proposal not found. Open the prospect record, Edit fields → Save, then try again.",
        ),
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token, slug, isPreview, isBarterPreview, uploadedClientId]);

  useEffect(() => {
    if (!isPreview && !isBarterPreview) load();
    else setLoading(false);
  }, [load, isPreview, isBarterPreview]);

  if (isBarterThanksPreview) {
    return <BarterAgreementPreview initialScreen="thanks" />;
  }
  if (isBarterPreview) return <BarterAgreementPreview initialScreen="agreement" />;

  if (isPreview) return <ScopeReviewPreview />;

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--t-bg)] p-6 md:p-12" data-testid="scope-review-page">
        <p className="t-meta">Loading proposal…</p>
      </div>
    );
  }

  // Server-backed live HTML proposal (works for logged-out prospects).
  if (slug && data?.options?.length) {
    return (
      <PublicLiveProposalReview
        slug={slug}
        data={data}
        onReload={(row) => {
          if (row) setData(row);
          else load();
        }}
      />
    );
  }

  // Local-only fallback (dev) when API has no published proposal yet.
  if (uploadedClientId && isLocalApp() && !data) {
    return <UploadedProposalReview clientId={uploadedClientId} />;
  }

  const respond = async (action) => {
    if (isStudio || !data?.can_respond || busy) return;
    const context = note.trim();
    const sig = signature.trim();
    const isBarter = data?.compensation_type === "barter";
    if (isBarter && action === "accept" && !sig) {
      toast.error("Draw your signature to continue.");
      return;
    }
    if (action === "request_changes" && !context) {
      toast.error("Add a note with what you’d like to share or change.");
      return;
    }
    setBusy(true);
    try {
      const path = slug
        ? `/public/proposal/${encodeURIComponent(slugifyClientName(slug))}/respond`
        : `/public/scope/${token}/respond`;
      const { data: row } = await api.post(path, {
        action,
        note: isBarter ? null : context || null,
        signature: isBarter && action === "accept" ? sig : null,
        option_index: data?.option_index ?? null,
        option_label: data?.option_label ?? null,
      });
      setData(row);
      if (action === "request_changes" && row?.response_note) {
        setNote(row.response_note);
      }
      toast.success(
        action === "accept"
          ? isBarter
            ? "Agreement signed."
            : data?.review_kind === "scope"
              ? "Scope accepted."
              : "Proposal accepted."
          : "Change request sent — we’ll adjust and follow up.",
      );
    } catch (e) {
      toast.error(formatApiDetail(e.response?.data?.detail, "Could not send response"));
    } finally {
      setBusy(false);
    }
  };

  const docUrl = slug
    ? `${resolveBackendOrigin()}/api/public/proposal/${encodeURIComponent(slugifyClientName(slug))}/document`
    : `${resolveBackendOrigin()}/api/public/scope/${token}/document`;

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[var(--t-bg)] p-6 md:p-12" data-testid="scope-review-page">
        <p className="text-sm text-[var(--t-error)]">{error || "Proposal not found"}</p>
      </div>
    );
  }

  const canRespond = Boolean(data.can_respond) && !isStudio;
  const liveStatus = data.status || "pending";
  const status = showStudioMocks ? studioMock : liveStatus;
  const isBarterDoc = data.compensation_type === "barter";
  const reviewKind =
    data.review_kind || (isBarterDoc ? "agreement" : data.has_live_proposal ? "proposal" : "scope");
  const docTitle =
    reviewKind === "agreement" ? "Agreement" : reviewKind === "scope" ? "Scope" : "Proposal";
  const invoiceUnlocked = Boolean(
    data.invoice_unlocked ?? (status === "accepted" && !isBarterDoc),
  );
  const acceptedAt =
    status === "accepted"
      ? data.accepted_at || new Date(Date.now() - 2 * 3600 * 1000).toISOString()
      : data.accepted_at;
  const acceptedOptionLabel =
    status === "accepted"
      ? data.accepted_option_label || (isBarterDoc ? "Agreement" : reviewKind === "scope" ? "Scope" : "Option 1")
      : data.accepted_option_label;
  const changesRequestedAt =
    status === "changes_requested"
      ? data.changes_requested_at || new Date(Date.now() - 90 * 60 * 1000).toISOString()
      : data.changes_requested_at;
  const responseNote =
    status === "changes_requested"
      ? data.response_note ||
        "Could we phase packaging templates into a second invoice? Identity + web first feels right for Q4."
      : data.response_note;

  const studioToggle = showStudioMocks ? (
    <div className="flex flex-wrap items-center gap-3" data-testid="scope-review-studio-mock">
      <button
        type="button"
        onClick={() => setStudioMock("pending")}
        className={`text-[11px] uppercase tracking-[0.14em] ${
          studioMock === "pending" ? "text-[var(--t-accent)]" : "text-[var(--t-meta)]"
        }`}
        data-testid="scope-studio-mock-proposal"
      >
        {docTitle}
      </button>
      <button
        type="button"
        onClick={() => setStudioMock("accepted")}
        className={`text-[11px] uppercase tracking-[0.14em] ${
          studioMock === "accepted" ? "text-[var(--t-accent)]" : "text-[var(--t-meta)]"
        }`}
        data-testid="scope-studio-mock-accepted"
      >
        {isBarterDoc ? "Thanks" : "Accepted"}
      </button>
      {!isBarterDoc ? (
        <button
          type="button"
          onClick={() => setStudioMock("changes_requested")}
          className={`text-[11px] uppercase tracking-[0.14em] ${
            studioMock === "changes_requested" ? "text-[var(--t-accent)]" : "text-[var(--t-meta)]"
          }`}
          data-testid="scope-studio-mock-changes"
        >
          Changes
        </button>
      ) : null}
    </div>
  ) : null;

  if (status === "accepted" && isBarterDoc) {
    return (
      <>
        {showStudioMocks ? (
          <div className="fixed left-0 right-0 top-0 z-20 flex flex-wrap items-center justify-end gap-3 px-4 py-3">
            {studioToggle}
          </div>
        ) : null}
        <ProposalThanksScreen
          clientName={data.client_name}
          optionLabel={acceptedOptionLabel}
          personName={data.signature_name || data.primary_contact_name || data.contact_name || ""}
          variant="barter"
        />
      </>
    );
  }

  return (
    <ScopeReviewShell
      studioToggle={studioToggle}
      title={docTitle}
      clientName={data.client_name}
      status={status}
      acceptedAt={acceptedAt}
      signedAt={data.signed_at || acceptedAt}
      signatureName={data.signature_name}
      acceptedOptionLabel={acceptedOptionLabel}
      changesRequestedAt={changesRequestedAt}
      responseNote={responseNote}
      canRespond={canRespond}
      canDownloadPdf={Boolean(data.can_download_pdf) && !isStudio}
      acceptLabel={isBarterDoc ? "Sign agreement" : reviewKind === "scope" ? "Accept scope" : "Accept this option"}
      signToAccept={isBarterDoc}
      onDownload={() => {
        window.location.href = docUrl;
      }}
      note={note}
      setNote={setNote}
      signature={signature}
      setSignature={setSignature}
      busy={busy}
      onAccept={() => respond("accept")}
      onRequestChanges={() => respond("request_changes")}
      documentNode={
        data.has_live_proposal || data.options?.length ? (
          <div data-testid="scope-token-document">
            <ScopeOptionsCarousel
              options={data.options || []}
              index={0}
              onIndexChange={() => {}}
              locked={status === "accepted" || isBarterDoc}
              sectionTitle={docTitle}
              canSelectAddon={false}
              showInvoice={!isBarterDoc}
              invoiceLocked={!invoiceUnlocked}
            />
          </div>
        ) : data.has_document ? (
          <div className="overflow-hidden rounded-2xl border border-[var(--t-border)] bg-white">
            <iframe
              title="Scope document"
              src={docUrl}
              className="h-[70vh] w-full"
              data-testid="scope-review-document"
            />
          </div>
        ) : (
          <p className="text-sm text-[var(--t-meta)]">
            Document will appear here once TAĪSTU attaches the scope packet.
          </p>
        )
      }
    />
  );
}
